import { inBounds, SIZE } from '../board.js';
import { mulberry32, randomInt, type Rng } from '../rng.js';
import type { FloePlacement, Lake, Rotation, Side, Square } from './types.js';

/** Version of the lake generator. Stored in game records; bump it on any change. */
export const LAKE_GENERATOR_VERSION = 1;

export const HOLE = -1;
export const MAX_THICKNESS = 3;
export const MIN_TERRITORY_SQUARES = 9;
const MAX_ATTEMPTS = 1000;

type Floe = readonly (readonly number[])[];

/**
 * The 12 double-sided floes. Rows run from the floe's rank-9 edge to its rank-1 edge at 0°.
 * Do not change these without bumping `LAKE_GENERATOR_VERSION`.
 */
// prettier-ignore
export const FLOES: readonly { code: string; A: Floe; B: Floe }[] = [
  { code: 'F01', A: [[0, 0, 1], [0, 1, 2], [1, 2, 3]], B: [[0, 0, 0], [0, 1, 0], [0, 0, 0]] },
  { code: 'F02', A: [[1, 1, 1], [1, 2, 1], [1, 1, 1]], B: [[0, -1, 0], [0, 0, 0], [1, 1, 0]] },
  { code: 'F03', A: [[2, 2, 1], [2, 3, 1], [1, 1, 0]], B: [[0, 0, 0], [1, 1, 1], [2, 2, 2]] },
  { code: 'F04', A: [[0, 1, 0], [1, -1, 1], [0, 1, 0]], B: [[1, 0, 1], [0, 0, 0], [1, 0, 1]] },
  { code: 'F05', A: [[0, 0, 0], [0, 2, 2], [0, 2, 3]], B: [[-1, 0, 0], [0, 0, 1], [0, 1, 1]] },
  { code: 'F06', A: [[1, 2, 1], [0, 1, 0], [0, 0, 0]], B: [[0, 0, 1], [0, 0, 1], [2, 1, 1]] },
  { code: 'F07', A: [[0, 1, 2], [0, 1, 2], [0, 1, 2]], B: [[0, 0, 0], [-1, 0, -1], [0, 0, 0]] },
  { code: 'F08', A: [[2, 1, 0], [1, 0, 0], [0, 0, 1]], B: [[0, 1, 1], [1, 2, 1], [1, 1, 0]] },
  { code: 'F09', A: [[1, 2, 1], [2, 3, 2], [1, 2, 1]], B: [[0, 0, 0], [0, 1, 0], [0, 0, -1]] },
  { code: 'F10', A: [[0, 0, 1], [-1, 0, 1], [0, 0, 2]], B: [[1, 1, 0], [1, 1, 0], [0, 0, 0]] },
  { code: 'F11', A: [[2, 2, 2], [1, 1, 1], [0, 0, 0]], B: [[0, 1, 0], [0, 1, 0], [0, 1, 0]] },
  { code: 'F12', A: [[0, 0, 0], [1, 2, -1], [0, 1, 0]], B: [[1, 0, 0], [2, 1, 0], [3, 2, 1]] },
];

const ROTATIONS: readonly Rotation[] = [0, 90, 180, 270];

export const STEPS_8: readonly (readonly [number, number])[] = [
  [-1, -1],
  [-1, 0],
  [-1, 1],
  [0, -1],
  [0, 1],
  [1, -1],
  [1, 0],
  [1, 1],
];

/** A floe turned clockwise by `rotation` degrees. */
export function rotateFloe(floe: Floe, rotation: Rotation): number[][] {
  let out = floe.map((row) => [...row]);
  for (let turn = 0; turn < rotation / 90; turn++) {
    const prev = out;
    out = [0, 1, 2].map((i) => [0, 1, 2].map((j) => prev[2 - j]?.[i] ?? 0));
  }
  return out;
}

/** Rows of `side`'s territory: Brand holds ranks 1–3 (rows 6–8), Rime ranks 7–9 (rows 0–2). */
export function territoryRows(side: Side): readonly number[] {
  return side === 'brand' ? [6, 7, 8] : [0, 1, 2];
}

export function inTerritory(side: Side, r: number): boolean {
  return territoryRows(side).includes(r);
}

/** The runestones, west to east: each is the list of its 9 squares. */
export const RUNESTONES: readonly (readonly Square[])[] = [0, 3, 6].map((c0) =>
  [3, 4, 5].flatMap((r) => [0, 1, 2].map((dc) => ({ r, c: c0 + dc }))),
);

/** Index of the runestone holding (r, c), or -1. */
export function runestoneOf(r: number, c: number): number {
  return r >= 3 && r <= 5 && c >= 0 && c < SIZE ? Math.floor(c / 3) : -1;
}

export function setHeight(heights: number[][], square: Square, height: number): void {
  const row = heights[square.r];
  if (!row || square.c < 0 || square.c >= SIZE) throw new RangeError('Square is off the lake');
  row[square.c] = height;
}

export function isHole(heights: number[][], r: number, c: number): boolean {
  return (heights[r]?.[c] ?? HOLE) === HOLE;
}

/** Squares (index `r * SIZE + c`) connected to `side`'s territory through non-hole squares. */
function reachableFrom(heights: number[][], side: Side): Set<number> {
  const seen = new Set<number>();
  const queue: Square[] = [];
  for (const r of territoryRows(side)) {
    for (let c = 0; c < SIZE; c++) {
      if (!isHole(heights, r, c)) {
        seen.add(r * SIZE + c);
        queue.push({ r, c });
      }
    }
  }
  for (const { r, c } of queue) {
    for (const [dr, dc] of STEPS_8) {
      const nr = r + dr;
      const nc = c + dc;
      if (inBounds(nr, nc) && !seen.has(nr * SIZE + nc) && !isHole(heights, nr, nc)) {
        seen.add(nr * SIZE + nc);
        queue.push({ r: nr, c: nc });
      }
    }
  }
  return seen;
}

/** Whether every non-hole square of every runestone connects to both territories. */
export function runestonesConnected(heights: number[][]): boolean {
  return (['brand', 'rime'] as const).every((side) => {
    const seen = reachableFrom(heights, side);
    return RUNESTONES.every((stone) =>
      stone.every(({ r, c }) => isHole(heights, r, c) || seen.has(r * SIZE + c)),
    );
  });
}

/** The generation checks: enough room to place, no stranded squares, connected runestones. */
export function lakeIsPlayable(heights: number[][]): boolean {
  for (const side of ['brand', 'rime'] as const) {
    let usable = 0;
    for (const r of territoryRows(side)) {
      for (let c = 0; c < SIZE; c++) {
        if (isHole(heights, r, c)) continue;
        usable++;
        const stranded = STEPS_8.every(
          ([dr, dc]) => !inBounds(r + dr, c + dc) || isHole(heights, r + dr, c + dc),
        );
        if (stranded) return false;
      }
    }
    if (usable < MIN_TERRITORY_SQUARES) return false;
  }
  return runestonesConnected(heights);
}

function drawLake(rng: Rng): { heights: number[][]; floes: FloePlacement[] } {
  const pool = [...FLOES];
  const heights = Array.from({ length: SIZE }, () => Array<number>(SIZE).fill(0));
  const floes: FloePlacement[] = [];
  for (let i = 0; i < 9; i++) {
    const [floe] = pool.splice(randomInt(rng, pool.length), 1);
    if (!floe) throw new Error('The floe pool is too small');
    const side = randomInt(rng, 2) === 0 ? 'A' : 'B';
    const rotation = ROTATIONS[randomInt(rng, ROTATIONS.length)] ?? 0;
    floes.push({ code: floe.code, side, rotation });
    const r0 = Math.floor(i / 3) * 3;
    const c0 = (i % 3) * 3;
    rotateFloe(floe[side], rotation).forEach((row, dr) =>
      row.forEach((h, dc) => setHeight(heights, { r: r0 + dr, c: c0 + dc }, h)),
    );
  }
  return { heights, floes };
}

/** Lake #seed. Draws continue from the same random sequence until a lake passes the checks. */
export function generateLake(seed: number): Lake {
  const rng = mulberry32(seed);
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    const { heights, floes } = drawLake(rng);
    if (lakeIsPlayable(heights)) return { seed, heights, floes };
  }
  throw new Error(`No playable lake for seed ${seed}`);
}

/** Lake from fixed thickness values (tests, tutorials). Checks only the shape. */
export function lakeFromHeights(heights: number[][], seed = 0): Lake {
  if (heights.length !== SIZE || heights.some((row) => row.length !== SIZE)) {
    throw new RangeError(`A lake is ${SIZE}×${SIZE}`);
  }
  for (const row of heights) {
    for (const h of row) {
      if (!Number.isInteger(h) || h < HOLE || h > MAX_THICKNESS) {
        throw new RangeError(`Bad thickness ${h}`);
      }
    }
  }
  return { seed, heights: heights.map((row) => [...row]), floes: [] };
}
